import React, { useMemo, useCallback } from 'react'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { EmptyCircleIcon } from '@/atoms/Icons/EmptyCircleIcon'
import { PhotonInput } from '@/atoms/PhotonInput'
import { CaptionMD, CaptionSM, Text } from '@/atoms/Text'
import { PIF_NAME_IN_CREDITS_MIN_USD } from '@/constants/pif'
import { slugs } from '@/constants/slugs'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { Offer } from '@/services/PifService'
import { getPeopleImpacted } from '@/services/PifService/PifService'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { formatNumberForDisplay } from '@/utils/numbers'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { Maybe } from '@/utils/types'
import { PerkStarIcon } from './PerkStarIcon'

interface TheatricalExpressPerkViewerProps {
  selectedOffer: Offer
  theatricalSlug: Maybe<string>
  projectName: string
  shouldShowDedicationExperiment?: boolean
  earlyAccessMinimumPrice?: number
  isExpressCheckout?: boolean
  newPifLayout?: boolean
  pifNameInCreditsEnabled?: boolean
}

const hasEarlyAccess = (offerPrice: number, earlyAccessMinimumPrice?: number) => {
  if (typeof earlyAccessMinimumPrice !== 'number') return false

  return offerPrice >= earlyAccessMinimumPrice
}

export const TheatricalExpressPerkViewer: React.FC<TheatricalExpressPerkViewerProps> = ({
  selectedOffer,
  earlyAccessMinimumPrice,
  isExpressCheckout,
  theatricalSlug,
  projectName,
  newPifLayout,
  pifNameInCreditsEnabled = false,
}) => {
  const { t } = useTranslate('pif')
  const { tributeOfferIDs } = useProjectMeta()
  const { offersData, handleSetSelectedOffer, nameInCredits, setNameInCredits } = usePayItForwardOfferData()

  const numPeopleImpacted = getPeopleImpacted(selectedOffer)
  const quantity = formatNumberForDisplay({ count: numPeopleImpacted })

  const filteredPerks = useMemo(() => {
    return selectedOffer?.offeredproductSet.filter((o) => {
      if (o?.product?.pif) return false
      if (tributeOfferIDs?.includes(o?.product?.id)) return false
      return true
    })
  }, [selectedOffer?.offeredproductSet, tributeOfferIDs])

  const selectedAmount = getPriceForCurrency(selectedOffer.price, selectedOffer.currency)
  const hasNameInCredits = selectedAmount >= PIF_NAME_IN_CREDITS_MIN_USD

  const targetOfferForNameInCredits = useMemo(() => {
    if (!offersData || !offersData.length) return null
    const sortedByPriceAsc = [...offersData].sort((a, b) => {
      const aPrice = getPriceForCurrency(a.price, a.currency)
      const bPrice = getPriceForCurrency(b.price, b.currency)
      return aPrice - bPrice
    })
    return (
      sortedByPriceAsc.find(
        (offer) => getPriceForCurrency(offer.price, offer.currency) >= PIF_NAME_IN_CREDITS_MIN_USD,
      ) || null
    )
  }, [offersData])

  const isNameInCreditsClickable = !hasNameInCredits && !!targetOfferForNameInCredits

  const handleClickNameInCredits = useCallback(() => {
    if (!isNameInCreditsClickable || !targetOfferForNameInCredits) return
    handleSetSelectedOffer(targetOfferForNameInCredits)
  }, [handleSetSelectedOffer, isNameInCreditsClickable, targetOfferForNameInCredits])

  const handleKeyDownNameInCredits = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isNameInCreditsClickable) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleClickNameInCredits()
      }
    },
    [isNameInCreditsClickable, handleClickNameInCredits],
  )

  const offerDescription =
    numPeopleImpacted === 0 ? null : (
      <Translate values={{ quantity }} t={t} i18nKey="payitForwardForNPeoplePerkTextV4">
        <span className="font-bold text-core-gray-950">Pay it Forward</span> (at least
        <span className="font-bold text-core-gray-950"> {{ quantity }}</span> movie tickets)
      </Translate>
    )

  const offerDescriptionV2 =
    numPeopleImpacted === 0 ? null : (
      <Translate values={{ quantity }} t={t} i18nKey="payitForwardForNPeoplePerkTextV5">
        <span className="font-bold text-core-gray-950">Pay it Forward</span>
        <span className="font-medium italic text-core-gray-950"> ({{ quantity }} movie tickets)</span>
      </Translate>
    )

  const nameInCreditsPerkDescription = (
    <Translate t={t} i18nKey="yourNameInCreditsPerkV2">
      <span className={`font-bold ${hasNameInCredits ? 'text-core-gray-950' : 'text-core-gray-400'}`}>
        Your Name in the Credits
      </span>
      <span className={`font-medium italic ${hasNameInCredits ? 'text-core-gray-950' : 'text-core-gray-400'}`}>
        {' '}
        ($500 or more)
      </span>
    </Translate>
  )

  return (
    <div className="my-6 flex flex-col gap-2">
      {filteredPerks && (
        <>
          {newPifLayout ? (
            <Text className="photon-title-sm md:photon-title-md font-semibold">
              {t('yourPerksAndImpact', 'Your Perks and Impact')}
            </Text>
          ) : (
            <CaptionMD weight="bold">{t('yourPerksAndImpact', 'Your Perks and Impact')}</CaptionMD>
          )}
          {offerDescription && (
            <>
              <li className="flex lg:items-start">
                <CircleCheckIcon className="mr-1.5 flex-none" color1="oxide-700" color2="white" />
                <CaptionMD color="core-gray-700">{newPifLayout ? offerDescriptionV2 : offerDescription}</CaptionMD>
              </li>
              {pifNameInCreditsEnabled && (
                <>
                  <li
                    className={`flex lg:items-start ${isNameInCreditsClickable ? 'cursor-pointer' : ''}`}
                    onClick={isNameInCreditsClickable ? handleClickNameInCredits : undefined}
                    onKeyDown={isNameInCreditsClickable ? handleKeyDownNameInCredits : undefined}
                    role={isNameInCreditsClickable ? 'button' : undefined}
                    tabIndex={isNameInCreditsClickable ? 0 : undefined}
                  >
                    <div className="mr-1.5 flex h-4 w-4 flex-none items-start">
                      {hasNameInCredits ? (
                        <PerkStarIcon color="core-copper" />
                      ) : (
                        <EmptyCircleIcon color1="core-gray-400" />
                      )}
                    </div>
                    <CaptionMD color={hasNameInCredits ? 'core-gray-700' : 'core-gray-400'}>
                      {nameInCreditsPerkDescription}
                    </CaptionMD>
                  </li>
                  {pifNameInCreditsEnabled && hasNameInCredits && (
                    <>
                      <div className="mt-2 flex w-[90%] flex-col gap-2 self-end">
                        <PhotonInput
                          name="nameInCredits"
                          label={t('nameInCreditsLabel', 'Enter your name for the credits')}
                          placeholder="Your name here"
                          minLength={3}
                          maxLength={40}
                          value={nameInCredits}
                          onChange={(e) => setNameInCredits(e.target.value)}
                        />
                        <CaptionSM color="core-warning-800" italic>
                          {t('personalOrFamilyNamesOnlyPlease', 'Personal or family names only please.')}
                        </CaptionSM>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
          {theatricalSlug === slugs.cabrini && isExpressCheckout && (
            <li className="mb-2 flex items-start lg:items-start">
              <CircleCheckIcon className="mr-1.5 flex-none" color1="oxide-700" color2="white" />
              <CaptionMD color="core-gray-700">
                <Translate t={t} i18nKey="exclusiveNewScenePerkV2">
                  <span className="font-bold text-core-gray-950">Exclusive New Scene</span> available after checkout
                </Translate>
              </CaptionMD>
            </li>
          )}
          {hasEarlyAccess(
            getPriceForCurrency(selectedOffer.price, selectedOffer.currency),
            earlyAccessMinimumPrice,
          ) && (
            <li className="mb-2 flex items-center lg:items-start">
              <CircleCheckIcon className="mr-1.5 flex-none" color1="oxide-700" color2="white" />
              <CaptionMD color="core-gray-700">
                <Translate t={t} i18nKey="earlyAccessWhenItReleasesInTheApp">
                  <span className="font-bold text-core-gray-950">Early Access</span> when it releases in the app
                </Translate>
              </CaptionMD>
            </li>
          )}
          {filteredPerks.map((perk) => {
            let perkText = perk.name

            if (perk?.product?.pif && perk?.quantity) {
              perkText = t('payItForwardForPeople', {
                count: perk.quantity,
                defaultValue_one: '{{quantity}} person can watch {{projectName}} for free',
                defaultValue_other: '{{quantity}} people can watch {{projectName}} for free',
                projectName,
                quantity: formatNumberForDisplay({ count: perk.quantity, format: '0[.]0a' }),
              })
            }
            return (
              <div key={perk?.id} className="flex items-start gap-2">
                <div className="my-0.5 h-4 w-4">
                  <CircleCheckIcon color1="oxide" color2="white" size={16} />
                </div>
                <CaptionMD className="my-0">{perkText}</CaptionMD>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
